import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatDate, statusColor } from "@/lib/format";

export default function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(null);

  const refresh = () => {
    api.get("/admin/tenants").then(({ data }) => setTenants(data));
    api.get("/admin/plans").then(({ data }) => setPlans(data));
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="admin-tenants-title">Tenants</h1>
          <p className="text-slate-500 text-sm mt-1">All businesses on the platform.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-create-tenant-btn"><Plus className="h-4 w-4 mr-2" /> New Tenant</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create Tenant</DialogTitle></DialogHeader>
            <CreateTenantForm onSaved={() => { setOpen(false); refresh(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-0 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Business</th>
              <th className="text-left px-4 py-3">Slug</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Expires</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-tenant-row-${t.slug}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.business_type} • {t.address}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">/t/{t.slug}</td>
                <td className="px-4 py-3">{t.plan?.name || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-xs">{formatDate(t.subscription_expires_at)}</td>
                <td className="px-4 py-3"><span className={`pill ${statusColor(t.status)}`}>{t.status}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" data-testid={`admin-tenant-${t.slug}-visit`} onClick={() => window.open(`/t/${t.slug}`, "_blank")}>
                      <ExternalLink className="h-3 w-3 mr-1" /> Storefront
                    </Button>
                    <Button size="sm" data-testid={`admin-tenant-${t.slug}-subscribe`} onClick={() => setSubOpen(t)}>Assign Plan</Button>
                    {t.status === "active" ? (
                      <Button size="sm" variant="outline" data-testid={`admin-tenant-${t.slug}-suspend`} onClick={async () => {
                        await api.post(`/admin/tenants/${t.id}/status?status=suspended`); refresh();
                      }}>Suspend</Button>
                    ) : (
                      <Button size="sm" variant="outline" data-testid={`admin-tenant-${t.slug}-activate`} onClick={async () => {
                        await api.post(`/admin/tenants/${t.id}/status?status=active`); refresh();
                      }}>Activate</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!subOpen} onOpenChange={(v) => !v && setSubOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign plan to {subOpen?.name}</DialogTitle></DialogHeader>
          {subOpen && <SubscribeForm tenant={subOpen} plans={plans} onSaved={() => { setSubOpen(null); refresh(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateTenantForm({ onSaved }) {
  const [form, setForm] = useState({ slug: "", name: "", business_type: "Rentals", owner_name: "", owner_phone: "", contact_email: "", address: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/tenants", form);
      toast.success("Tenant created");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="create-tenant-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Slug</Label><Input data-testid="tenant-slug-input" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="e.g. mybiz" /></div>
        <div><Label>Business Name</Label><Input data-testid="tenant-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
      </div>
      <div><Label>Business Type</Label><Input data-testid="tenant-type-input" value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Owner Name</Label><Input data-testid="tenant-owner-name-input" required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} /></div>
        <div><Label>Owner Phone</Label><Input data-testid="tenant-owner-phone-input" required value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} /></div>
      </div>
      <div><Label>Contact Email</Label><Input data-testid="tenant-email-input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
      <div><Label>Address</Label><Input data-testid="tenant-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
      <Button disabled={loading} type="submit" className="w-full" data-testid="tenant-save-btn">{loading ? "Saving..." : "Create Tenant"}</Button>
    </form>
  );
}

function SubscribeForm({ tenant, plans, onSaved }) {
  const [planId, setPlanId] = useState(plans[0]?.id || "");
  const [months, setMonths] = useState(1);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!planId) { toast.error("Pick a plan"); return; }
    setLoading(true);
    try {
      await api.post(`/admin/tenants/${tenant.id}/subscribe`, { plan_id: planId, months });
      toast.success("Subscription assigned");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-3">
      <div>
        <Label>Plan</Label>
        <Select value={planId} onValueChange={setPlanId}>
          <SelectTrigger data-testid="subscribe-plan-select"><SelectValue placeholder="Pick plan" /></SelectTrigger>
          <SelectContent>
            {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} – ₹{p.price}/{p.billing_cycle}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div><Label>Duration (cycles)</Label><Input data-testid="subscribe-months-input" type="number" min={1} value={months} onChange={(e) => setMonths(parseInt(e.target.value || "1"))} /></div>
      <Button disabled={loading} className="w-full" onClick={submit} data-testid="subscribe-confirm-btn">{loading ? "Processing..." : "Charge (Mock) & Assign"}</Button>
    </div>
  );
}
